import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, Select, Skeleton, Space,
  Table, Tag, Typography, message,
} from 'antd';
import { DateField, StudentPickerField, TextField } from '../../../ui/fields';
import { Field, FormModal, FormSection } from '../../../ui/forms';
import { PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import type { BonafideKind, BonafideRow, IssueBonafideRequest } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin — Bonafide + character certificates.
 * List + issue-new + printable card (@media print A4).
 */
const KIND_LABELS: Record<BonafideKind, string> = {
  bonafide: 'Bonafide', character: 'Character', custom: 'Custom',
};

export function BonafidePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [printFor, setPrintFor] = useState<BonafideRow | null>(null);

  const list = useQuery({
    queryKey: ['bonafide', institution],
    queryFn: () => productApi.listBonafide(institution),
    enabled: !!institution,
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div className="bc-no-print" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Bonafide certificates</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Bonafide / character / custom. Non-terminal — the student remains enrolled.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Issue certificate</Button>
      </div>

      <Card className="bc-no-print" size="small" title={`${list.data?.total ?? 0} certificates`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {(list.data?.rows.length ?? 0) === 0 && !list.isLoading && (
          <Empty description="No certificates yet." style={{ padding: 20 }} />
        )}
        {(list.data?.rows.length ?? 0) > 0 && (
          <Table<BonafideRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={list.data?.rows ?? []}
            columns={[
              { title: 'Cert no.', dataIndex: 'cert_number', key: 'n', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Kind', dataIndex: 'kind', key: 'k', width: 100, render: (v: BonafideKind) => <Tag color={v === 'character' ? 'purple' : 'processing'}>{KIND_LABELS[v]}</Tag> },
              { title: 'Student', dataIndex: 'student_name', key: 's', render: (v: string, r) => <><b>{v}</b> <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {r.admission_number}</Typography.Text></> },
              { title: 'Purpose', dataIndex: 'purpose', key: 'p', ellipsis: true },
              { title: 'Issued', dataIndex: 'issue_date', key: 'i', width: 110 },
              { title: 'Valid until', dataIndex: 'valid_until', key: 'vu', width: 110, render: (v: string | null) => v ?? <Typography.Text type="secondary">no expiry</Typography.Text> },
              {
                key: 'a', width: 100, align: 'right',
                render: (_, r) => <Button size="small" icon={<PrinterOutlined />} onClick={() => setPrintFor(r)}>Print</Button>,
              },
            ]}
          />
        )}
      </Card>

      {printFor && <PrintView row={printFor} onBack={() => setPrintFor(null)} />}

      <IssueModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['bonafide', institution] });
          void message.success('Certificate issued');
        }}
      />
      <style>{PRINT_CSS}</style>
    </Space>
  );
}

function PrintView({ row, onBack }: { row: BonafideRow; onBack: () => void }) {
  return (
    <>
      <div className="bc-no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onBack}>Back to list</Button>
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>Print (A4)</Button>
      </div>
      <div className="bc-page">
        <header className="bc-header">
          <div className="bc-inst-name">{row.institution_name}</div>
          <div className="bc-doc-title">{row.kind === 'character' ? 'Character Certificate' : row.kind === 'bonafide' ? 'Bonafide Certificate' : row.purpose}</div>
          <div className="bc-number">No. <span className="bc-mono">{row.cert_number}</span></div>
        </header>
        <section className="bc-body">
          <p>
            This is to certify that <b>{row.student_name}</b> (Admission Number{' '}
            <span className="bc-mono">{row.admission_number}</span>) is a bona fide student of this
            institution, currently studying in <b>{row.grade}{row.section ? `-${row.section}` : ''}</b>.
          </p>
          {row.body ? (
            <p style={{ whiteSpace: 'pre-wrap' }}>{row.body}</p>
          ) : (
            <p>
              This certificate is issued for the purpose of <b>{row.purpose}</b>
              {row.valid_until ? <> and is valid until <b>{row.valid_until}</b></> : null}.
            </p>
          )}
        </section>
        <footer className="bc-footer">
          <div className="bc-sig"><div className="bc-line" /><div>Class teacher</div></div>
          <div className="bc-sig"><div className="bc-line" /><div>Principal</div></div>
          <div className="bc-sig"><div className="bc-line" /><div>Institution seal</div></div>
        </footer>
        <div className="bc-fine">Issued by {row.issued_by ?? 'the institution'} on {row.issue_date}.</div>
      </div>
    </>
  );
}

type BonafideFormValues = { admission_number: string; kind: BonafideKind; purpose: string; issue_date: string; valid_until?: string; body?: string };

function IssueModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <FormModal<BonafideFormValues>
      open={open} onClose={onClose}
      title="Issue certificate" submitText="Issue"
      initial={{ kind: 'bonafide', issue_date: today, purpose: 'Passport / visa application' }}
      mutation={(v) => productApi.issueBonafide(institution, {
        admission_number: v.admission_number,
        kind: v.kind,
        purpose: v.purpose,
        issue_date: v.issue_date,
        valid_until: v.valid_until || undefined,
        body: v.body || undefined,
      } satisfies IssueBonafideRequest)}
      onSuccess={onCreated}
    >
      <Field name="admission_number" label="Student" required>
        <StudentPickerField institution={institution} />
      </Field>
      <FormSection cols={3}>
        <Field name="kind" label="Kind" required>
          <Select options={(Object.keys(KIND_LABELS) as BonafideKind[]).map((k) => ({ value: k, label: KIND_LABELS[k] }))} />
        </Field>
        <Field name="issue_date" label="Issue date" required>
          <DateField />
        </Field>
        <Field name="valid_until" label="Valid until" optional>
          <DateField />
        </Field>
      </FormSection>
      <Field name="purpose" label="Purpose" required>
        <TextField />
      </Field>
      <Field name="body" label="Custom body (overrides default text)" optional>
        <Input.TextArea rows={4} placeholder="Free-form text if needed" />
      </Field>
    </FormModal>
  );
}

const PRINT_CSS = `
  .bc-page {
    background: #fff; color: #14213d;
    max-width: 794px; margin: 0 auto; padding: 34px 44px;
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    border: 1px solid #eeeef1; border-radius: 4px; min-height: 800px;
  }
  .bc-header { text-align: center; border-bottom: 2px solid #14213d; padding-bottom: 14px; margin-bottom: 24px; }
  .bc-inst-name { font-size: 24px; font-weight: 700; letter-spacing: 0.5px; }
  .bc-doc-title { font-size: 16px; margin-top: 6px; letter-spacing: 4px; text-transform: uppercase; color: #5b6478; }
  .bc-number { font-size: 12px; margin-top: 4px; color: #5b6478; }
  .bc-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
  .bc-body p { font-size: 14px; line-height: 1.8; margin-bottom: 18px; }
  .bc-footer { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; margin-top: 70px; }
  .bc-sig { text-align: center; font-size: 11.5px; color: #5b6478; }
  .bc-line { border-top: 1px solid #14213d; margin: 40px 24px 8px; }
  .bc-fine { margin-top: 30px; font-size: 10.5px; color: #8a94a6; text-align: center; font-style: italic; }
  @media print {
    @page { size: A4; margin: 15mm; }
    body { background: #fff; }
    .bc-no-print, .ant-message, .ant-notification { display: none !important; }
    .bc-page { box-shadow: none; border: none; margin: 0; padding: 0; max-width: none; }
  }
`;
